import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';

const ModalMovimientoManual = ({ show, onHide, rfcSeleccionado, idDatoFiscal, onSuccess }) => {
    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    const [fecha, setFecha] = useState(new Date());
    const [concepto, setConcepto] = useState('');
    const [referencia, setReferencia] = useState('');
    const [monto, setMonto] = useState('');
    const [tipo, setTipo] = useState('I');
    const [cuentas, setCuentas] = useState([]);
    const [cuentaSeleccionada, setCuentaSeleccionada] = useState('');
    const [cargandoCuentas, setCargandoCuentas] = useState(false);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (show && idDatoFiscal) {
            cargarCuentasBancarias();
            // Reset form
            setFecha(new Date());
            setConcepto('');
            setReferencia('');
            setMonto('');
            setTipo('I');
            setCuentaSeleccionada('');
        }
    }, [show, rfcSeleccionado]);

    const cargarCuentasBancarias = async () => {
        try {
            setCargandoCuentas(true);
            const response = await fetch('/api/contabilidad/bancos/Conciliacion/ObtenerCuentasBancariasPorEmpresa', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ idDatoFiscal: idDatoFiscal })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const cuentasParse = JSON.parse(data.detalles || '[]');
                    setCuentas(cuentasParse);
                    if (cuentasParse.length > 0) {
                        setCuentaSeleccionada(cuentasParse[0].idCatCuentaBancaria.toString());
                    }
                } else {
                    showErrorToast(data.vMessage || 'Error al obtener cuentas');
                }
            } else {
                showErrorToast('Error al conectar con el servidor');
            }
        } catch (error) {
            console.error(error);
            showErrorToast('Error al cargar cuentas bancarias');
        } finally {
            setCargandoCuentas(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!cuentaSeleccionada) {
            showErrorToast('Debe seleccionar una cuenta bancaria');
            return;
        }

        try {
            setGuardando(true);
            const response = await fetch('/api/contabilidad/bancos/conciliacion/AgregarMovimientoManual', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    idCuentaBancaria: cuentaSeleccionada,
                    fecha: fecha,
                    concepto: concepto,
                    referencia: referencia,
                    monto: parseFloat(monto),
                    tipo: tipo
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    showSuccessToast(data.vMessage || 'Movimiento agregado correctamente');
                    if (onSuccess) onSuccess();
                    onHide();
                } else {
                    showErrorToast(data.vMessage || 'Error al guardar el movimiento');
                }
            } else {
                showErrorToast('Error al conectar con el servidor');
            }
        } catch (error) {
            console.error(error);
            showErrorToast('Error de conexión al guardar');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fs-1 fw-bold">
                    <i className="fas fa-plus-circle text-success me-2"></i>
                    Agregar Movimiento Manual
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {!rfcSeleccionado && (
                        <div className="alert alert-warning py-2 fs--1">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Asegúrese de haber seleccionado una empresa en el filtro principal.
                        </div>
                    )}
                    <Row className="g-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="fs--1 fw-semi-bold">Cuenta Bancaria</Form.Label>
                                {cargandoCuentas ? (
                                    <div className="d-flex align-items-center"><Spinner animation="border" size="sm" className="me-2" /> Cargando cuentas...</div>
                                ) : (
                                    <Form.Select
                                        size="sm"
                                        value={cuentaSeleccionada}
                                        onChange={(e) => setCuentaSeleccionada(e.target.value)}
                                        required
                                    >
                                        <option value="">Seleccione una cuenta</option>
                                        {cuentas.map(c => (
                                            <option key={c.idCatCuentaBancaria} value={c.idCatCuentaBancaria}>
                                                {c.vNombreBanco} - {c.vNumCta} ({c.vMoneda})
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fs--1 fw-semi-bold">Fecha</Form.Label>
                                <DatePicker
                                    selected={fecha}
                                    onChange={(date) => setFecha(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control form-control-sm"
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fs--1 fw-semi-bold">Tipo</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={tipo}
                                    onChange={(e) => setTipo(e.target.value)}
                                >
                                    <option value="I">Ingreso</option>
                                    <option value="E">Egreso</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="fs--1 fw-semi-bold">Concepto / Descripción</Form.Label>
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Ej. Pago factura 123"
                                    value={concepto}
                                    onChange={(e) => setConcepto(e.target.value)}
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fs--1 fw-semi-bold">Referencia (Opcional)</Form.Label>
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    value={referencia}
                                    onChange={(e) => setReferencia(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fs--1 fw-semi-bold">Monto</Form.Label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">$</span>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        placeholder="0.00"
                                        value={monto}
                                        onChange={(e) => setMonto(e.target.value)}
                                        required
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="bg-light border-0">
                    <Button variant="secondary" size="sm" onClick={onHide}>
                        Cancelar
                    </Button>
                    <Button variant="success" size="sm" type="submit" disabled={!rfcSeleccionado || guardando || cargandoCuentas}>
                        {guardando ? (
                            <><Spinner animation="border" size="sm" className="me-1" /> Guardando...</>
                        ) : (
                            <><i className="fas fa-save me-1"></i> Guardar Movimiento</>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};



ModalMovimientoManual.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    rfcSeleccionado: PropTypes.string,
    idDatoFiscal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSuccess: PropTypes.func
};

export default ModalMovimientoManual;
